/**
 * Legacy Sendbird v3 connection handler middleware.
 *
 * Restored from git (c5c927eeb^). Updated to:
 * - Read REACT_APP_SENDBIRD_APP_ID_LEGACY instead of REACT_APP_SENDBIRD_APP_ID
 * - Short-circuit when isNewChatSelector returns true (new UIKit v4 mode)
 * - Import v3-only actions/selectors from legacy/ directory
 */

import { nanoid } from 'nanoid';

import {
  sbCreatInstance,
  sbConnect,
  sbDisconnect,
  sbGetInstance,
  CONNECTED,
  FAILED,
  RECONNECTING,
} from 'sendbird-utils';

import { messageReceiveFactory } from 'sendbird-utils/lib/imAdapters';
import { PushNotificationPayloadAdapter } from '@/domain/chat/legacy/helpers';
import {
  chatTokenSelector,
  userIdSelector,
  newChatFlowSelector,
  signOutAction,
  signInAction,
  isDokkaSupportSelector,
} from '@/domain/env';

import { messagesLoadedSelector, isOpenSelector, isNewChatSelector } from '@/domain/chat/chatSelector';
import { chatThreadSelector, isOneSignalInitSelector } from '@/domain/chat/legacy/chatSelector';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { getOrganizationFeatures } from '@/domain/organization/organizationActions';

import * as A from '@/domain/chat/legacy/chatActions';
import { setOpenAction, onInitOneSignalAction } from '@/domain/chat/chatActions';

const WS_CLOSE_STATUS = 'CLOSED';

function isWsConnectionClose() {
  return sbGetInstance().getConnectionState() === WS_CLOSE_STATUS;
}

function sbConnectAction(dispatch, type = 'success', userId) {
  dispatch({
    type: A.chatConnectAction[type],
    userId,
  });
}

function initPushNotificationObserver(handler) {
  if (handler.pushObserverAttached) return;
  handler.pushObserverAttached = true;

  window.OneSignal = window.OneSignal || [];
  const { OneSignal } = window;

  OneSignal.push(() => {
    OneSignal.setSubscription(true);
  });

  OneSignal.push(() => {
    OneSignal.on('notificationDisplay', (e) => {
      handler.onNotificationDisplay(e);
    });
  });

  OneSignal.push(() => {
    OneSignal.on('notificationPermissionChange', (permissionChange) => {
      const currentPermission = permissionChange.to;
      if (currentPermission === 'granted') OneSignal.setSubscription(true);
    });
  });

  document.addEventListener('visibilitychange', () => {
    const isHidden = document.visibilityState === 'hidden';
    if (isHidden) {
      handler.onBrowserHide();
    } else {
      handler.onBrowserActive();
    }
  });
}

class HandlerClass {
  constructor({ dispatch, getState }) {
    this.handlerId = nanoid(10);
    this.isBrowserActive = true;
    this._dispatch = dispatch;
    this._getState = getState;
    // tryConnect is intentionally NOT called here — lazyInit will call
    // resolveConnection() which decides connect-vs-disconnect based on current
    // state. Calling tryConnect here races against that decision: the
    // sbConnect promise resolves later and dispatches allowedWorker(false),
    // pausing polling that resolveConnection just started.
  }

  _dispatch;

  _getState;

  handlerId;

  isBrowserActive;

  _messageReceive = messageReceiveFactory(process.env.REACT_APP_ENV || 'local');

  onChatStatusOpenChange() {
    const isOpen = isOpenSelector(this._getState());
    if (isOpen) {
      this.onOpenChat();
    } else {
      this.onCloseChat();
    }
  }

  onOneSignalInit() {
    this.resolveConnection();
  }

  onThreadsLoaded() {
    setTimeout(this.resolveConnection.bind(this), 200);
  }

  onGetUserProfile() {
    this.resolveConnection();
  }

  onOpenChat() {
    this.resolveConnection();
  }

  onCloseChat() {
    this.resolveConnection();
  }

  onBrowserHide() {
    this.isBrowserActive = false;
    this.resolveConnection();
  }

  onBrowserActive() {
    this.isBrowserActive = true;
    this.resolveConnection();
  }

  onNotificationDisplay(event) {
    window.OneSignal = window.OneSignal || [];
    const { OneSignal } = window;
    if (this.isAllowPushConnection()) {
      this.receiveMessage(PushNotificationPayloadAdapter(event));
    } else {
      OneSignal.push(() => {
        OneSignal.setSubscription(false);
      });
    }
  }

  disconnectServer() {
    if (!isWsConnectionClose()) {
      sbDisconnect();
    }
    // Always enable the worker — we want polling regardless of whether
    // Sendbird was previously connected. Gating on !wsClosed dropped the
    // dispatch on initial load (Sendbird never connected yet), so polling
    // never started.
    this._dispatch({
      type: A.setAllowedChatWorkerAction.type,
      payload: true,
    });
  }

  resolveConnection() {
    window.OneSignal = window.OneSignal || [];
    const { OneSignal } = window;
    const isAllowSocketConnection = this.isAllowSocketConnection();
    const isAllowPushConnection = this.isAllowPushConnection();

    if (isAllowSocketConnection) {
      this.tryConnect();
    } else {
      this.disconnectServer();
    }

    OneSignal.push(() => {
      OneSignal.setSubscription(isAllowPushConnection);
    });
  }

  isAllowSocketConnection = () => {
    const state = this._getState();
    const isOneSignalInit = isOneSignalInitSelector(state);
    const isNewChatFlow = newChatFlowSelector(state);
    const isLoadedChannels = messagesLoadedSelector(state);
    const isOpenChat = isOpenSelector(state);
    const result = !isNewChatFlow || !isLoadedChannels || (isOpenChat && isNewChatFlow) || !isOneSignalInit;
    return result;
  };

  isAllowPushConnection = () => {
    const state = this._getState();
    const isSocketConnection = this.isAllowSocketConnection();
    const isOneSignalInit = isOneSignalInitSelector(state);
    return isOneSignalInit ? !isSocketConnection || !this.isBrowserActive : false;
  };

  tryConnect() {
    const state = this._getState();
    const accessToken = chatTokenSelector(state);
    const userId = userIdSelector(state);
    const isGhost = isDokkaSupportSelector(state);
    const wsClosed = isWsConnectionClose();
    // `this.isSocketConnectionStatus` was referenced here historically but never
    // assigned anywhere — `!undefined` was always true, so the guard was dead
    // code. Dropped to keep the condition honest.
    const canConnect = wsClosed && accessToken && userId && !isGhost;
    if (canConnect) {
      return sbConnect(userId, accessToken).then(() => {
        this._dispatch({
          type: A.chatConnectAction.success,
          userId,
        });
        this._dispatch({
          type: A.setAllowedChatWorkerAction.type,
          payload: false,
        });
      });
    }
    return Promise.reject().catch(() => {
      sbConnectAction(this._dispatch, 'failure');
    });
  }

  messageHandler = (channel, message) => {
    let payload = this._messageReceive(channel, [message]);
    if (payload) {
      if (this._getState().chat.activeChannel === payload.name) {
        channel.markAsRead();
        payload = payload.set('unreadMessageCount', 0);
      }
      this.receiveMessage(payload);
    }
  };

  receiveMessage = (payload) => {
    if (this.isNoExistsMessage(payload)) {
      this._dispatch({
        type: A.receiveMessageAction.success,
        payload,
      });
    }
  };

  isNoExistsMessage = (payload) => {
    const threads = chatThreadSelector(this._getState());
    const currentThread = threads.get(payload.name);
    if (!currentThread) return true;

    const existsMessageIds = currentThread.get('messages').map((m) => m.messageId);
    return payload.get('messages').every((newMessages) => !existsMessageIds.includes(newMessages.messageId));
  };

  _connectionStatus(status) {
    this._dispatch({
      type: A.setChatConnectionStatusAction.type,
      payload: status,
    });
  }

  reconnectStartedHandler = () => {
    this._connectionStatus(RECONNECTING);
  };

  reconnectSucceededHandler = () => {
    this._connectionStatus(CONNECTED);
  };

  reconnectFailedHandler = () => {
    this._connectionStatus(FAILED);
  };

  add() {
    if (!this.ConnectionHandler && !this.ChannelHandler) {
      const sbInst = sbGetInstance();
      this.ConnectionHandler = new sbInst.ConnectionHandler();
      this.ChannelHandler = new sbInst.ChannelHandler();
      this.ConnectionHandler.onReconnectStarted = this.reconnectStartedHandler;
      this.ConnectionHandler.onReconnectSucceeded = this.reconnectSucceededHandler;
      this.ConnectionHandler.onReconnectFailed = this.reconnectFailedHandler;
      this.ChannelHandler.onMessageReceived = this.messageHandler;
      sbInst.addConnectionHandler(this.handlerId, this.ConnectionHandler);
      sbInst.addChannelHandler(this.handlerId, this.ChannelHandler);
    }
  }

  remove() {
    if (this.ConnectionHandler && this.ChannelHandler) {
      const sbInst = sbGetInstance();
      sbInst.removeConnectionHandler(this.handlerId);
      sbInst.removeChannelHandler(this.handlerId);
      this.ConnectionHandler = null;
      this.ChannelHandler = null;
    }
  }
}

export default function chatMiddleware() {
  return (store) => {
    // Defer initialization: org features aren't loaded at store creation time.
    // We lazily initialize the v3 Sendbird SDK on first signInAction.success,
     // then short-circuit forever if enable_chat_v2 is true.
    let handler = null;
    let initialized = false;
    let isNewChat = false;

    function lazyInit() {
      if (initialized) return;
      initialized = true;

      isNewChat = isNewChatSelector(store.getState());
      if (isNewChat) return; // new chat mode — skip all v3 operations

      const appId = getRuntimeConfig().env.REACT_APP_SENDBIRD_APP_ID_LEGACY;
      if (!appId) return;

      sbCreatInstance(appId);
      handler = new HandlerClass(store);
      handler.add();

      // Replay missed OneSignal init: onInitOneSignalAction likely fired before
      // lazyInit (before getOrganizationFeatures.success) while handler was
      // null, so initPushNotificationObserver was never called. The reducer
      // already set isOneSignalInit=true — replay the observer setup now.
      // Replay missed lifecycle: actions like signInAction / onInitOneSignal /
      // chatGetThreadsAction may have fired before lazyInit ran (before
      // getOrganizationFeatures.success), so their handlers were skipped while
      // handler was null. State flags (isOneSignalInit, loaded) were still set
      // by reducers. If OneSignal already initialized, replay the observer
      // setup that was missed. resolveConnection() reads current state and
      // dispatches either tryConnect or disconnectServer.
      if (isOneSignalInitSelector(store.getState())) {
        initPushNotificationObserver(handler);
      }
      handler.resolveConnection();
    }

    return (next) => (action) => {
      // Let the reducer process the action first so state is updated
      const result = next(action);

      // Initialize after org features are loaded (reducer has set features by now)
      if (action.type === getOrganizationFeatures.success) {
        lazyInit();
      }

      // If new chat mode or not yet initialized, skip v3 handling
      if (isNewChat || !handler) {
        return result;
      }

      switch (action.type) {
        case signOutAction.success:
          sbDisconnect();
          store.dispatch({
            type: A.setAllowedChatWorkerAction.type,
            payload: false,
          });
          break;

        case setOpenAction.type:
          setTimeout(() => {
            handler.onChatStatusOpenChange();
          }, 0);
          break;

        case onInitOneSignalAction.type:
          setTimeout(() => {
            initPushNotificationObserver(handler);
            handler.onOneSignalInit();
          }, 0);
          break;

        case A.chatGetThreadsAction.success:
          handler.onThreadsLoaded();
          break;

        case signInAction.success:
          setTimeout(() => {
            handler.onGetUserProfile();
          }, 0);
          break;

        default:
          break;
      }
      return result;
    };
  };
}
